import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, ChevronRight, Send, CheckCircle2 } from "lucide-react";
import { getApplyContext, submitApplication } from "@/lib/jobsApi";

/**
 * Iter-263 §3: Quick Apply 4-step wizard.
 *   1. Documents (resume + optional cover letter)
 *   2. Screening questions
 *   3. Profile review (contact + per-application right-to-work override)
 *   4. Submit
 */
const STEPS = ["Documents", "Questions", "Review", "Submit"];

export default function JobApplyPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [resumeId, setResumeId] = useState(null);
  const [coverId, setCoverId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [rtwOverride, setRtwOverride] = useState("");
  const [portfolioUrl, setPortfolioUrl] = useState("");
  const [portfolioError, setPortfolioError] = useState("");

  const q = useQuery({ queryKey: ["apply-context", id], queryFn: () => getApplyContext(id) });

  // Prime the RTW override + question prefill answers from the profile
  // and question spec once the context loads.
  useEffect(() => {
    const rtw = q.data?.profile?.right_to_work_status;
    if (rtw && !rtwOverride) setRtwOverride(rtw);
    // Seed answers from question.prefill_answer so previously-supplied
    // profile values (e.g. right-to-work) don't force the applicant to
    // re-select what we already know.
    const qs = q.data?.questions || [];
    if (qs.length) {
      setAnswers((prev) => {
        const next = { ...prev };
        for (const qq of qs) {
          if (qq.prefill_answer != null && next[qq.id] == null) {
            next[qq.id] = qq.prefill_answer;
          }
        }
        return next;
      });
    }
  }, [q.data?.profile?.right_to_work_status, q.data?.questions]);

  const submitMut = useMutation({
    mutationFn: () => {
      const trimmed = portfolioUrl.trim();
      const payload = {
        resume_document_id: resumeId,
        cover_letter_document_id: coverId,
        answers: Object.entries(answers).map(([qid, a]) => ({ question_id: qid, answer: a })),
        right_to_work_status: rtwOverride || undefined,
      };
      // Iter-265 §5: omit `portfolio_url` when unset (backend normalises
      // whitespace to null; sending the empty key would only add noise).
      if (trimmed) payload.portfolio_url = trimmed;
      return submitApplication(id, payload);
    },
    onSuccess: () => { toast.success("Application submitted."); nav("/jobs/applications"); },
    onError: (err) => {
      const s = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (s === 422 && typeof detail === "string" && detail.toLowerCase().includes("portfolio")) {
        // Backend 422 for `portfolio_url` scheme uses the exact string
        // "Portfolio link must start with http:// or https://" — surface
        // it verbatim inline next to the field and via toast.
        setPortfolioError(detail);
        setStep(0);
        toast.error(detail);
        return;
      }
      if (s === 409) toast.error(detail || "Cannot apply — profile incomplete, duplicate application, or listing inactive.");
      else if (s === 422) toast.error("Please answer all required questions.");
      else toast.error(detail || "Submit failed");
    },
  });

  if (q.isLoading) return <Shell><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></Shell>;
  if (q.isError) return <Shell><p className="text-red-500">Could not load. <Link to={`/ad/${id}`} className="underline">Back to listing</Link></p></Shell>;

  const ctx = q.data;
  if (ctx.is_own_listing) return <Shell><p className="text-slate-500">You cannot apply to your own listing. <Link to={`/jobs/employer?listing=${id}`} className="underline">Manage applicants</Link></p></Shell>;
  if (ctx.already_applied) return <Shell><p className="text-slate-500">You have already applied. <Link to="/jobs/applications" className="underline">View my application</Link></p></Shell>;
  if (!ctx.profile_complete) return <Shell><p className="text-slate-500">Complete your profile before applying. <Link to="/jobs/profile" className="underline text-[color:var(--brand-blue)]">Complete profile →</Link></p></Shell>;

  const questions = ctx.questions || [];
  const docs = ctx.documents || [];
  const resumes = docs.filter((d) => d.doc_type === "resume");
  const covers = docs.filter((d) => d.doc_type === "cover_letter");

  const canAdvance = () => {
    if (step === 0) {
      if (!resumeId) return false;
      const t = portfolioUrl.trim();
      // Portfolio is optional; if set, gate advancement on client-side
      // http(s) validation to match the backend 422 rule exactly.
      if (t && !/^https?:\/\//i.test(t)) return false;
      return true;
    }
    if (step === 1) return questions.filter((qq) => qq.required).every((qq) => {
      const v = answers[qq.id];
      return Array.isArray(v) ? v.length : (v != null && v !== "");
    });
    return true;
  };

  return (
    <Shell>
      <div className="mb-6">
        <Link to={`/ad/${id}`} className="text-sm text-slate-500 hover:text-[color:var(--brand-blue)]">← Back to job</Link>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-black text-slate-900" data-testid="apply-title">
          Apply — {ctx.job?.title}
        </h1>
      </div>
      {/* Stepper */}
      <ol className="flex items-center gap-2 text-xs mb-6" data-testid="apply-stepper">
        {STEPS.map((label, i) => (
          <li key={label} className={`flex items-center gap-2 ${i === step ? "text-[color:var(--brand-blue)] font-bold" : "text-slate-400"}`}>
            <span className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-bold ${i === step ? "bg-[color:var(--brand-blue)] text-white" : "bg-slate-100"}`}>{i + 1}</span>
            {label}
            {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-300" />}
          </li>
        ))}
      </ol>

      <div className="rounded-2xl bg-white border border-soft p-5 md:p-6">
        {step === 0 && (
          <div data-testid="step-documents" className="space-y-4">
            <h2 className="font-display font-bold text-lg">Documents</h2>
            <DocPicker label="Resume" items={resumes} value={resumeId} onChange={setResumeId} />
            <DocPicker label="Cover letter (optional)" items={covers} value={coverId} onChange={setCoverId} allowNone />
            <p className="text-xs text-slate-400">Need to upload? <Link to="/jobs/profile#docs" className="underline text-[color:var(--brand-blue)]">Manage documents</Link></p>
            <div>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Portfolio / work sample (optional)</span>
                <input
                  data-testid="apply-portfolio-url"
                  type="url"
                  inputMode="url"
                  placeholder="https://your-portfolio.example.com"
                  className="input"
                  value={portfolioUrl}
                  onChange={(e) => {
                    setPortfolioUrl(e.target.value);
                    if (portfolioError) setPortfolioError("");
                  }}
                  onBlur={() => {
                    const t = portfolioUrl.trim();
                    if (t && !/^https?:\/\//i.test(t)) {
                      setPortfolioError("Portfolio link must start with http:// or https://");
                    }
                  }}
                />
              </label>
              {portfolioError && (
                <p className="mt-1 text-xs text-red-500" data-testid="apply-portfolio-error">{portfolioError}</p>
              )}
              <p className="mt-1 text-[11px] text-slate-400">
                Optional — share a link the employer can review (personal site, GitHub, Dribbble, Google Drive).
              </p>
            </div>
          </div>
        )}
        {step === 1 && (
          <div data-testid="step-questions" className="space-y-4">
            <h2 className="font-display font-bold text-lg">Screening questions</h2>
            {questions.length === 0 && <p className="text-slate-500 text-sm">No screening questions for this role.</p>}
            {questions.map((qq) => (
              <QuestionField key={qq.id} q={qq} value={answers[qq.id]} onChange={(v) => setAnswers((s) => ({ ...s, [qq.id]: v }))} />
            ))}
          </div>
        )}
        {step === 2 && (
          <div data-testid="step-review" className="space-y-4">
            <h2 className="font-display font-bold text-lg">Review your profile</h2>
            <div className="flex items-center gap-3">
              {ctx.profile.photo_url && <img src={ctx.profile.photo_url} alt="" className="w-14 h-14 rounded-full object-cover ring-2 ring-slate-200" />}
              <div>
                <div className="font-bold text-slate-800">{ctx.profile.full_name}</div>
                <div className="text-xs text-slate-500">{ctx.profile.email} · {ctx.profile.phone}</div>
              </div>
            </div>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1 block">Right-to-work status (override)</span>
              <select data-testid="review-rtw-override" className="input" value={rtwOverride || ctx.profile.right_to_work_status || ""} onChange={(e) => setRtwOverride(e.target.value)}>
                <option value="citizen">NZ citizen</option>
                <option value="resident">NZ permanent resident</option>
                <option value="visa">Work visa</option>
                <option value="other">Other</option>
              </select>
            </label>
            <p className="text-xs text-slate-400">Edit full profile: <Link to="/jobs/profile" className="underline text-[color:var(--brand-blue)]">/jobs/profile</Link></p>
            {portfolioUrl.trim() && (
              <div className="rounded-xl border border-subtle p-3" data-testid="review-portfolio-row">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Portfolio / work sample</div>
                <a
                  href={portfolioUrl.trim()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm break-all text-[color:var(--brand-blue)] hover:underline"
                  data-testid="review-portfolio-link"
                >
                  {portfolioUrl.trim()}
                </a>
              </div>
            )}
          </div>
        )}
        {step === 3 && (
          <div data-testid="step-submit" className="text-center py-6">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <h2 className="font-display font-bold text-xl mt-3">Ready to submit?</h2>
            <p className="text-sm text-slate-500 mt-1">Your profile, documents and answers will be snapshotted for the employer.</p>
            <button data-testid="apply-submit-btn" onClick={() => submitMut.mutate()} disabled={submitMut.isPending} className="mt-4 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-6 py-3 disabled:opacity-70">
              {submitMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit application
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <button data-testid="apply-back-btn" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="text-sm font-semibold text-slate-500 disabled:opacity-40">← Back</button>
        {step < 3 && (
          <button data-testid="apply-next-btn" disabled={!canAdvance()} onClick={() => setStep((s) => s + 1)} className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] disabled:opacity-40 text-white font-bold px-5 py-2">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) { return <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 md:py-12">{children}</div>; }
function DocPicker({ label, items, value, onChange, allowNone }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="space-y-1">
        {items.length === 0 && <p className="text-slate-400 text-sm">No documents in your library yet.</p>}
        {items.map((d) => (
          <label key={d.id} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" data-testid={`pick-doc-${d.id}`} checked={value === d.id} onChange={() => onChange(d.id)} />
            <span>{d.file_name}{d.is_default && " · default"}</span>
          </label>
        ))}
        {allowNone && (
          <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer">
            <input type="radio" data-testid={`pick-doc-none-${label.toLowerCase().replace(/[^a-z]/g, "")}`} checked={value === null} onChange={() => onChange(null)} />
            <span>Do not attach a {label.toLowerCase()}</span>
          </label>
        )}
      </div>
    </div>
  );
}
function QuestionField({ q, value, onChange }) {
  const t = q.question_type;
  return (
    <div data-testid={`q-${q.id}`} className="rounded-xl border border-subtle p-3">
      <div className="text-sm font-semibold text-slate-800">{q.question_text}{q.required && <span className="text-red-500 ml-1">*</span>}</div>
      {t === "text" && (
        <textarea data-testid={`q-input-${q.id}`} className="input mt-2 min-h-20" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
      {t === "yes_no" && (
        <div className="mt-2 flex gap-3">
          {["Yes", "No"].map((v) => (
            <label key={v} className="inline-flex items-center gap-1.5 text-sm">
              <input type="radio" data-testid={`q-yn-${q.id}-${v}`} checked={value === v} onChange={() => onChange(v)} />{v}
            </label>
          ))}
        </div>
      )}
      {t === "single_select" && (
        <select data-testid={`q-select-${q.id}`} className="input mt-2" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">— select —</option>
          {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      )}
      {t === "multi_select" && (
        <div className="mt-2 space-y-1">
          {(q.options || []).map((o) => (
            <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid={`q-multi-${q.id}-${o}`} checked={(value || []).includes(o)} onChange={(e) => {
                const cur = Array.isArray(value) ? value : [];
                onChange(e.target.checked ? [...cur, o] : cur.filter((x) => x !== o));
              }} />{o}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
